import { useId } from 'react'
import { moduleVisual } from '~/helpers/learner/module-visual'
import { cn } from '~/lib/utils'

/**
 * Bandeau visuel d'une carte de formation (#74, option A).
 *
 * Purement décoratif : `aria-hidden`, aucun texte, aucune information qui ne
 * soit dite ailleurs. Un lecteur d'écran n'a rien à y lire, et surtout l'état de
 * la formation ne doit PAS y être encodé — sur une échelle de statut la couleur
 * porte le sens, et ce dégradé est celui de la marque, pas celui du statut.
 *
 * L'encre des motifs est `--color-primary-foreground`, le seul jeton dont on
 * sache qu'il contraste avec la couleur de marque : c'est elle qui domine le
 * premier arrêt du dégradé. Du blanc en dur disparaîtrait chez un organisme aux
 * teintes claires, et du noir chez un organisme sombre.
 */
function ModuleArtwork({ moduleId, className }: { moduleId: string, className?: string }) {
  const { background, pattern } = moduleVisual(moduleId)
  const patternId = useId()

  return (
    <div
      aria-hidden="true"
      className={cn('relative overflow-hidden', className)}
      style={{ background }}
    >
      <svg
        className="absolute inset-0 size-full"
        viewBox="0 0 400 160"
        preserveAspectRatio="xMidYMid slice"
        fill="none"
        style={{ color: 'var(--color-primary-foreground)' }}
      >
        {pattern === 0 && (
          // Arcs concentriques, ancrés hors cadre : ce qui sort du cadre suggère
          // une forme plus grande que la carte, et évite le motif « logo centré ».
          <g stroke="currentColor" strokeOpacity="0.16" strokeWidth="2" fill="none">
            {[40, 80, 120, 160, 200].map(r => (
              <circle key={r} cx="360" cy="24" r={r} />
            ))}
          </g>
        )}

        {pattern === 1 && (
          <>
            <defs>
              <pattern id={patternId} width="22" height="22" patternUnits="userSpaceOnUse">
                <circle cx="3" cy="3" r="1.8" fill="currentColor" fillOpacity="0.22" />
              </pattern>
            </defs>
            <rect width="400" height="160" fill={`url(#${patternId})`} />
          </>
        )}

        {pattern === 2 && (
          <>
            <defs>
              <pattern
                id={patternId}
                width="26"
                height="26"
                patternUnits="userSpaceOnUse"
                patternTransform="rotate(35)"
              >
                <rect width="9" height="26" fill="currentColor" fillOpacity="0.13" />
              </pattern>
            </defs>
            <rect width="400" height="160" fill={`url(#${patternId})`} />
          </>
        )}

        {pattern === 3 && (
          <g fill="currentColor" fillOpacity="0.14">
            <circle cx="330" cy="112" r="78" />
            <circle cx="252" cy="34" r="46" />
            <circle cx="66" cy="140" r="58" />
          </g>
        )}
      </svg>
    </div>
  )
}

export default ModuleArtwork
